import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { ChangeTuitionPayments, type KeptPayment } from './ChangeTuitionPayments';
import { centsToDollars, dollarsToCents } from './moneyInput';
import { useKeepTuitionAgreement } from './useKeepTuitionAgreement';
import { useSetTuitionAgreement } from './useSetTuitionAgreement';
import { useTuitionTermsPreview, type TuitionTermsChoices } from './useTuitionTermsPreview';

export interface TuitionPlan {
  key: string;
  name: string;
}

export interface SetTuitionItem {
  id: string;
  title: string;
  tuitionAgreement?: {
    agreementId: string;
    state?: string;
    paymentOptionKey?: string | null;
    aidCents: number;
    aidReason?: string | null;
    supersedesAgreementId?: string | null;
  } | null;
}

interface SetTuitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: SetTuitionItem;
  /** Every plan of the year, the ones families can't choose included. */
  plans: TuitionPlan[];
  /** The family's year, for Keep tuition as it is. */
  familyEnrollmentId?: string;
  onSaved?: () => void;
}

const DEBOUNCE_MS = 300;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// The office sets a family's tuition: the plan, an amount off with its reason,
// the payment amounts, and any payment left off auto-pay. The payments come
// from the server as the office changes any of these, so what is offered is
// what was shown; its words for a problem (payments that don't add up) show
// here, and Offer the agreement waits until there is none. On a signed agreement, or
// a change to one waiting to be signed, it is Change tuition: the payments that stay
// are shown beside the new ones, and on a signed one the office can keep the
// agreement as it is instead.
export function SetTuitionDialog({ open, onOpenChange, item, plans, familyEnrollmentId = '', onSaved }: SetTuitionDialogProps) {
  const agreement = item.tuitionAgreement;
  const signed = agreement?.state === 'SIGNED';
  const changing = signed || !!agreement?.supersedesAgreementId;
  const [planKey, setPlanKey] = useState(agreement?.paymentOptionKey && plans.some((p) => p.key === agreement.paymentOptionKey) ? agreement.paymentOptionKey : (plans[0]?.key ?? ''));
  const [aidText, setAidText] = useState(agreement && agreement.aidCents > 0 ? centsToDollars(agreement.aidCents) : '');
  const [reason, setReason] = useState(agreement?.aidReason ?? '');
  // The agreement the dialog opened with, kept if the page refetches: the server refuses it once another has replaced it.
  const [agreementId] = useState(agreement?.agreementId ?? null);
  // Whether it was signed then: one the family signs while the dialog is open is refused as replaced.
  const [agreementSigned] = useState(signed);
  // Typed payment amounts once the office has edited one; null while the server's even split stands.
  const [amountTexts, setAmountTexts] = useState<string[] | null>(null);
  const [offAutoPay, setOffAutoPay] = useState<number[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [setTuition, { loading: saving }] = useSetTuitionAgreement();
  const [keepTuition, { loading: keeping }] = useKeepTuitionAgreement();
  const [keepReason, setKeepReason] = useState('');

  const aidCents = aidText.trim() === '' ? 0 : dollarsToCents(aidText);
  const amounts = useMemo(() => {
    if (!amountTexts) return null;
    const cents = amountTexts.map((text) => dollarsToCents(text));
    return cents.every((c): c is number => c !== null) ? (cents as number[]) : undefined;
  }, [amountTexts]);

  const choices: TuitionTermsChoices | null =
    planKey && aidCents !== null && amounts !== undefined ? { paymentOptionKey: planKey, aidCents, amounts, offAutoPay } : null;
  const choicesKey = JSON.stringify(choices);
  const debouncedKey = useDebounced(choicesKey, DEBOUNCE_MS);
  const debounced = useMemo<TuitionTermsChoices | null>(() => JSON.parse(debouncedKey), [debouncedKey]);
  const settled = debouncedKey === choicesKey;
  const { data, loading: previewing } = useTuitionTermsPreview(item.id, debounced);
  const preview = data?.tuitionTermsPreview ?? null;

  // The payment rows keep their dates while the office types an amount that doesn't add up.
  const lastRows = useRef<{ dueOn: string; amountCents: number }[]>([]);
  if (preview && !preview.error && preview.installments.length > 0) lastRows.current = preview.installments.map((i) => ({ dueOn: i.dueOn, amountCents: i.amountCents }));
  const rows = lastRows.current;
  const lastKept = useRef<KeptPayment[]>([]);
  if (preview && !preview.error) lastKept.current = (preview.kept ?? []).map((k) => ({ dueOn: k.dueOn, amountCents: k.amountCents, status: k.status }));
  const kept = changing ? lastKept.current : [];
  const shownAmounts = amountTexts ?? rows.map((row) => centsToDollars(row.amountCents));

  const localError =
    aidCents === null ? 'Enter the amount off in dollars' : amounts === undefined ? 'Give each payment an amount in dollars' : null;
  const error = localError ?? (settled ? preview?.error ?? null : null);
  const reasonMissing = (aidCents ?? 0) > 0 && !reason.trim();
  const canOffer = !saving && !!agreementId && !!choices && settled && !previewing && !!preview && !preview.error && !reasonMissing;

  const changePlan = (key: string) => {
    setPlanKey(key);
    setAmountTexts(null);
    setOffAutoPay([]);
  };

  const changeAid = (text: string) => {
    setAidText(text);
    setAmountTexts(null);
  };

  const changeAmount = (index: number, text: string) => {
    const base = amountTexts ?? rows.map((row) => centsToDollars(row.amountCents));
    setAmountTexts(base.map((value, i) => (i === index ? text : value)));
  };

  const toggleOffAutoPay = (index: number, off: boolean) => {
    setOffAutoPay((current) => (off ? [...current, index].sort((a, b) => a - b) : current.filter((i) => i !== index)));
  };

  const handleKeep = async () => {
    if (!keepReason.trim()) return;
    try {
      const result = await keepTuition({ variables: { familyEnrollmentId, reason: keepReason.trim() } });
      const serverErrors = result.data?.keepTuitionAgreement?.userErrors ?? [];
      if (serverErrors.length > 0) {
        setErrors(serverErrors.map((e) => e.message));
        return;
      }
      setErrors([]);
      onOpenChange(false);
      onSaved?.();
    } catch (caught) {
      setErrors([caught instanceof Error ? caught.message : 'Tuition was not kept. Please try again.']);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canOffer || !choices || !agreementId) return;
    try {
      const result = await setTuition({
        variables: {
          input: {
            itemId: item.id,
            agreementId,
            agreementSigned,
            paymentOptionKey: choices.paymentOptionKey,
            aidCents: choices.aidCents,
            aidReason: reason.trim() || null,
            amounts: choices.amounts,
            offAutoPay: choices.offAutoPay,
          },
        },
      });
      const serverErrors = result.data?.setTuitionAgreement?.errors ?? [];
      if (serverErrors.length > 0) {
        setErrors([...serverErrors]);
        return;
      }
      setErrors([]);
      onOpenChange(false);
      onSaved?.();
    } catch (caught) {
      setErrors([caught instanceof Error ? caught.message : 'Tuition was not set. Please try again.']);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) setErrors([]); onOpenChange(next); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{changing ? 'Change tuition' : 'Set tuition'}</DialogTitle>
          <DialogDescription>{item.title}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div role="alert" className="space-y-1 text-sm text-destructive">
              {errors.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="tuition-plan">Plan</Label>
              <select
                id="tuition-plan"
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={planKey}
                onChange={(e) => changePlan(e.target.value)}
              >
                {plans.map((plan) => (
                  <option key={plan.key} value={plan.key}>{plan.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="tuition-aid">Amount off for the year</Label>
              <Input id="tuition-aid" inputMode="decimal" placeholder="0.00" value={aidText} onChange={(e) => changeAid(e.target.value)} />
            </div>
          </div>
          {(aidCents ?? 0) > 0 && (
            <div className="space-y-1">
              <Label htmlFor="tuition-reason">Reason</Label>
              <Textarea id="tuition-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              <p className="text-xs text-muted-foreground">Stays with the office; the family never sees it.</p>
            </div>
          )}

          <div className="space-y-2">
            <ChangeTuitionPayments
              kept={kept}
              payments={rows}
              paymentsTitle={changing ? 'New payments' : 'Payments'}
              totalCents={settled && preview && !preview.error ? preview.totalCents : null}
              renderPayment={(_payment, index) => (
                <>
                  <Input
                    aria-label={`Payment ${index + 1} amount`}
                    className="h-8 w-32"
                    inputMode="decimal"
                    value={shownAmounts[index] ?? ''}
                    onChange={(e) => changeAmount(index, e.target.value)}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      aria-label={`Leave payment ${index + 1} off auto-pay`}
                      checked={offAutoPay.includes(index)}
                      onChange={(e) => toggleOffAutoPay(index, e.target.checked)}
                    />
                    Leave off auto-pay
                  </label>
                </>
              )}
            />
            {error && (
              <p role="alert" className="text-sm text-destructive">{error}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canOffer}>
              {changing ? 'Offer the change' : 'Offer the agreement'}
            </Button>
          </DialogFooter>
        </form>
        {signed && (
          <div className="space-y-2 border-t pt-4">
            <p className="text-sm text-muted-foreground">The signed agreement stays as it is. Adjust the family's invoices by hand if needed.</p>
            <Label htmlFor="tuition-keep-reason">Why it stays as it is</Label>
            <Textarea id="tuition-keep-reason" value={keepReason} onChange={(e) => setKeepReason(e.target.value)} />
            <Button type="button" variant="outline" disabled={keeping || !keepReason.trim()} onClick={handleKeep}>
              Keep tuition as it is
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SetTuitionDialog;
